<template>
  <div
    class="node"
    :class="
      [
        selected(),
        node.name,
        hasWarning ? 'warning' : '',
        hasError ? 'error' : '',
        isDisabled ? 'disabled' : '',
      ] | kebab
    "
  >
    <div class="title">
      <img class="icon" src="../assets/icons/effect.svg">
      <div class="title-text">
        <span>{{ titleText }}</span>
        <md-tooltip 
          v-if="shouldMountComplexElements()" 
          md-direction="top"
          data-lod-complex="tooltip"
        >
          Effect: {{ titleText }}
        </md-tooltip>
      </div>

      <help-tooltip 
        v-if="shouldMountComplexElements() && hasWarning" 
        type="warning"
        data-lod-complex="warning-tooltip"
      >
        <span v-for="warning in warnings" :key="warning.message">
          {{ warning.message }}
          <br>
        </span>
      </help-tooltip>
      <help-tooltip 
        v-if="shouldMountComplexElements() && hasError" 
        type="error"
        data-lod-complex="error-tooltip"
      >
        <span v-for="error in errors" :key="error.message">
          {{ error.message }}
          <br>
        </span>
      </help-tooltip>

      <div 
        v-if="shouldMountComplexElements() && editor.isAdmin" 
        class="nodeId"
        data-lod-complex="node-id"
      >
        ID:{{ node.id }}
      </div>
    </div>

    <div 
      v-if="shouldMountComplexElements()"
      data-lod-complex="controls-container"
    >
      <segmented-select
        :items="availableControls()"
        :value="state.activeControlType"
        :title-text="titleText"
        segment-by="superCategory"
        placeholder="Then..."
        :continuous-columns="true"
        :disabled="state.readonly"
        data-lod-complex="segmented-select"
        @select="control => updateActiveControl(control.value)"
      />

      <div
        v-for="control in controls()"
        :key="control.key"
        v-control="control"
        class="control"
        data-lod-complex="control-item"
      />

      <div v-if="hasExpiry()" class="expiry-container" data-lod-complex="expiry-container">
        <el-input
          :value="state.expiry"
          placeholder="∞"
          :controls="false"
          class="expiry-input"
          type="number"
          :disabled="state.readonly"
          data-lod-complex="expiry-input"
          @input="expiry => updateExpiry(expiry)"
        >
          <template slot="prepend">Expires after</template>
          <template slot="append">s</template>
        </el-input>
      </div>
    </div>

    <div class="sockets-container">
      <div v-for="input in inputs()" :key="input.key" class="input">
        <Socket
          v-socket:input="input"
          class="socket"
          type="input"
          :socket="input.socket"
        />
        <div v-show="!input.showControl()" class="input-title">
          {{ input.name }}
        </div>
        <div
          v-show="input.showControl()"
          v-control="input.control"
          class="input-control"
        />
      </div>

      <div v-if="hasOutput" class="output">
        <div v-for="output in outputs()" :key="output.key" class="output">
          <div class="output-title">{{ output.name }}</div>
          <Socket
            v-socket:output="output"
            class="socket"
            type="output"
            :socket="output.socket"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ReteVueRenderPlugin from 'rete-vue-render-plugin';
import findKey from 'lodash/findKey';
import get from 'lodash/get';
import uniqBy from 'lodash/uniqBy';
import sortBy from 'lodash/sortBy';
import intersection from 'lodash/intersection';
import toArray from 'lodash/toArray';

import HelpTooltip from '../../common/components/HelpTooltip';
import SegmentedSelect from '../components/SegmentedSelect.vue';
import EFFECT_CONTROLS from '../EFFECT_CONTROLS';
import { getControlDescriptionForType } from '../utils/getControlForType';
import getRecommendedControls from '../utils/getRecommendedControls';

export default {
  name: 'effect',
  components: {
    Socket: ReteVueRenderPlugin.Socket,
    HelpTooltip,
    SegmentedSelect,
  },
  mixins: [ReteVueRenderPlugin.mixin],
  props: {},
  data() {
    return {
      EFFECT_CONTROLS,
      state: {
        activeControlType: undefined,
        activeControl: null,
        expiry: undefined,
        readonly: false,
        hasInputConnection: true,
        hasOutputConnection: true,
      },
    };
  },
  computed: {
    controlDescriptor() {
      return this.EFFECT_CONTROLS[this.state.activeControlType] || { label: 'Effect' };
    },
    titleText() {
      return this.controlDescriptor.label;
    },
    hasOutput() {
      const { activeControlType } = this.state;
      if (!activeControlType) {
        return true;
      }
      const activeControlDescription = this.getControlDescription(
        activeControlType,
        this.node.data.effect,
      );
      return !activeControlDescription.noOutput;
    },
    hasWarning() {
      return this.warnings && this.warnings.length;
    },
    hasError() {
      return this.errors && this.errors.length;
    },
    isDisabled() {
      return this.controlDescriptor.isDisabled && this.controlDescriptor.isDisabled(this.editor.caseConf);
    },
    isValid() {
      return this.state.hasInputConnection;
    },
    warnings() {
      if (!this.editor.$store) {
        return [];
      }
      return uniqBy(
        get(
          this.editor.$store.getters['validation/visibleWarningsByFieldAndId'],
          `caseLogicDescription.${this.node.id}`,
        ),
        'message',
      );
    },
    errors() {
      if (!this.editor.$store) {
        return [];
      }
      return uniqBy(
        get(
          this.editor.$store.getters['validation/errorsByFieldAndId'],
          `caseLogicDescription.${this.node.id}`,
        ),
        'message',
      );
    },
    chainableComponents() {
      return intersection(
        ['Check', 'Effect', 'Timer', 'EffectChain'],
        toArray(this.editor.components.keys()),
      );
    },
  },
  watch: {},
  created() {
    this.resetControls();
    const { data } = this.node;
    this.state.expiry = data.expiry;

    if (data.effect) {
      this.updateActiveControl(data.effect.type, false);
    }
    this.state.readonly = this.editor.readonly;
    this.editor.on('readonly', readonly => {
      this.state.readonly = readonly;
    });
  },
  beforeDestroy() {
    if (this.state.activeControl) {
      // Rete does not delete vue instances when node is destroyed
      if (this.state.activeControl._vue) {
        this.state.activeControl._vue.$destroy();
      }
    }
  },
  methods: {
    // LOD methods
    isLodSimplified() {
      return this.node._lodState ? this.node._lodState.isSimplified : false;
    },
    shouldRenderComplexElements() {
      return !this.isLodSimplified();
    },
    hasBeenRenderedComplex() {
      return this.node._lodState ? this.node._lodState.hasBeenRenderedComplex : false;
    },
    useHybridRendering() {
      return this.node._lodState ? this.node._lodState.useHybridRendering : false;
    },
    // Hybrid rendering method: use v-if initially, then v-show after first complex render
    shouldMountComplexElements() {
      if (!this.useHybridRendering()) {
        return this.shouldRenderComplexElements();
      }
      // If using hybrid rendering, mount elements if they've been rendered complex at least once
      return this.hasBeenRenderedComplex();
    },
    shouldShowComplexElements() {
      if (!this.useHybridRendering()) {
        return this.shouldRenderComplexElements();
      }
      // If using hybrid rendering and elements are mounted, show/hide via CSS
      return this.hasBeenRenderedComplex() && this.shouldRenderComplexElements();
    },
    availableControls() {
      return sortBy(
        getRecommendedControls(
          this.editor.caseConf,
          EFFECT_CONTROLS,
          this.editor.isGlobal,
        ),
        control =>
          ['Enable', 'Disable', 'Change', 'Play', 'Grading'].indexOf(
            control.superCategory,
          ),
      )
      .filter(control => !control.isDisabled || !control.isDisabled(this.editor.caseConf))
      .map(control => ({
        ...control,
        value: control.key,
        class: control.recommended
          ? 'control-option recommended'
          : 'control-option',
        // For most effects, super categories are simply the verb of the effect
        // To avoid repetition of the verb we take it out of the dropdown
        dropdownLabel: control.label.replace(control.superCategory, '').trim(),
        superCategory: control.label.includes(control.superCategory)
          ? control.superCategory + '...'
          : control.superCategory,
      }));
    },
    resetControls() {
      if (this.node.controls.get('effect')) {
        const currentControl = this.node.controls.get('effect');
        this.node.removeControl(currentControl);
        // Rete does not delete vue instances when controls are removed
        if (currentControl._vue) {
          currentControl._vue.$destroy();
        }
      }
    },
    updateActiveControl(controlType, resetData = true) {
      if (controlType === this.state.activeControlType) {
        return;
      }
      this.state.activeControlType = controlType;
      this.resetControls();

      // For some reason removing a control and adding one in
      // the same stack frame causes the node to not update
      setTimeout(() => {
        const { activeControlType } = this.state;
        const { effect } = this.node.data;
        const EffectControl = this.getControlDescription(
          activeControlType,
          effect,
        ).control;
        this.state.activeControl = new EffectControl(this.editor, 'effect');

        // The selection dropdown won't display correctly when loading an effect type
        // with multiple controls from saved node data. We need to look up the key,
        // and re-assign the activeControlType.
        if (!EFFECT_CONTROLS[activeControlType]) {
          this.state.activeControlType = findKey(
            EFFECT_CONTROLS,
            a => a.control === EffectControl,
          );
        }

        if (resetData) {
          this.node.data = {};
        }
        this.node.addControl(this.state.activeControl);

        // remove existing outputs if this control is not supposed to have outputs (e.g. end case)
        if (!this.hasOutput) {
          this.node.outputs.forEach(output =>
            output.connections.forEach(connection => {
              this.editor.removeConnection(connection);
            }),
          );
        }
        this.$forceUpdate();
        this.editor.trigger('process', this.node);
        if (resetData) {
          const effectControlDescription = getControlDescriptionForType(
            activeControlType,
            effect,
            EFFECT_CONTROLS,
          );
          this.$analytics.event(
            'Authoring Tool',
            'effect_selected',
            effectControlDescription && effectControlDescription.title,
          );
        }
      });
    },
    updateExpiry(expiry) {
      const expiryInt = parseInt(expiry, 10) || undefined;
      this.state.expiry = expiryInt;
      this.node.data.expiry = expiryInt;
      this.editor.trigger('process', this.node);
    },
    getControlDescription(effectType, effect) {
      return getControlDescriptionForType(
        effectType,
        effect || {},
        EFFECT_CONTROLS,
      );
    },
    // This could be made into a computed property but
    // unfortunately the node data does not come as an
    // observable object, so vue does not update this component
    // when it changes
    hasExpiry() {
      const { effect } = this.node.data;
      const { activeControlType } = this.state;
      if (!activeControlType) {
        return false;
      }
      if (this.getControlDescription(activeControlType, effect).noExpiry) {
        return false;
      }
      if (
        effect &&
        (effect.type === 'VITAL' || effect.type === 'LAB_MODIFY') &&
        effect.isAbsolute
      ) {
        return false;
      }
      if (effect && effect.type === 'ANIMATION_ONE_SHOT') {
        return false;
      }
      return true;
    },
  },
};
</script>

<style scoped>
.node {
  background: rgb(56, 180, 194);
  border-radius: 10px;
  cursor: pointer;
  min-width: 400px;
  padding-bottom: 6px;
  box-sizing: content-box;
  position: relative;
  user-select: none;
  display: flex;
  flex-direction: column;
  padding: 0 5px;
  box-sizing: border-box;
}
.node:hover {
  background: rgba(56, 180, 194, 0.8);
}

.node.highlighted {
  background: #fbd430;
  border-color: #fbd430;
}
.node.selected {
  box-shadow: 0 3px 6px 0 rgba(255, 255, 255, 0.7);
  border: solid 4px #ffffff;
}
.node.disabled {
  background: #525252;
}
.node .icon {
  width: 16px;
  height: 16px;
  display: inline;
}
.node .title {
  color: white;
  font-family: sans-serif;
  font-size: 18px;
  padding: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.node .title .title-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  padding-left: 4px;
}
.node .nodeId {
  float: right;
}
.sockets-container {
  display: flex;
  justify-content: space-between;
}
.node .socket.input {
  background: url('../assets/icons/active-input.svg');
  background-size: 100%;
  background-repeat: no-repeat;
  margin-left: 0;
}
.node .socket.output {
  background: url('../assets/icons/active-output.svg');
  background-size: 100%;
  background-repeat: no-repeat;
  margin-right: 0;
}
.node .socket {
  border: none;
}
.node .socket:hover {
  border: solid;
}
.node .output {
  text-align: right;
}
.node .input {
  text-align: left;
}
.node .input-title,
.node .output-title {
  vertical-align: middle;
  color: white;
  display: inline-block;
  font-family: sans-serif;
  font-size: 14px;
  margin: 6px;
  line-height: 24px;
}
.node .input-control {
  z-index: 1;
  width: calc(100% - 36px);
  vertical-align: middle;
  display: inline-block;
}
.node .control {
  padding: 6px 18px;
}
.expiry-container {
  padding: 0 18px;
  padding-bottom: 8px;
  align-self: flex-end;
}
.expiry-input {
  width: 210px;
}
.node.error {
  border: solid 3px #f92c2d;
}
.node.warning {
  border: solid 3px #f2994a;
}

.node >>> .control-option:not(.recommended) {
  color: #a9b3bc;
}
</style>
